<template>
   <div class="searchable-dropdown">
      <div class="searchable-input-wrapper">
         <input
            ref="searchInput"
            type="text"
            autocomplete="off"
            v-model="searchQuery"
            @input="handleInputImmediate"
            @keydown="handleKeydown"
            class="searchable-input"
            @focus="handlerInputFocused"
            @blur="handlerInputBlurred"
            :class="{ 'invisible-input': shouldHideInput }" />

         <!-- <div class="clear-button" v-if="searchQuery.length > 0">
            <i class="gts-icon icon-close-rounded icon-xs" @click="clearInput" />
         </div> -->
      </div>

      <div class="searchable-results" v-if="hasResults && !shouldHideInput">
         <!-- Search History Section -->
         <div class="result-section history-section" v-if="searchHistory.length > 0">
            <div class="section-icon">
               <i class="gts-icon icon-latest-searches-point-ac" />
            </div>
            <div class="section-content">
               <div class="section-title">
                  <span>{{ $t("hotelAutocomplete_lblLastSearches") }}</span>
               </div>
               <div class="result-list">
                  <div class="result-item" v-for="(item, index) in searchHistory" :key="'history-' + index" v-html="highlightText(item.name)" @click="selectResult(item, true)"></div>
               </div>
            </div>
         </div>

         <!-- District Section -->
         <div class="result-section district-section" v-if="filteredDistricts.length > 0">
            <div class="section-icon">
               <i class="gts-icon icon-location-point-ac" />
            </div>
            <div class="section-content">
               <div class="section-title">
                  <span>{{ $t("hotelAutocomplete_lblDisctrict") }}</span>
               </div>
               <div class="result-list">
                  <div
                     class="result-item"
                     v-for="(item, index) in filteredDistricts"
                     :key="'district-' + index"
                     :class="{ highlighted: highlightedIndex === index && highlightedSection === 'district' }"
                     v-html="highlightText(item.name)"
                     @click="selectResult(item)"></div>
               </div>
            </div>
         </div>

         <!-- Hotel Section -->
         <div class="result-section hotel-section" v-if="filteredHotels.length > 0">
            <div class="section-icon">
               <i class="gts-icon icon-hotel-point-ac" />
            </div>
            <div class="section-content">
               <div class="section-title">
                  <span>{{ $t("hotelAutocomplete_lblHotel") }}</span>
               </div>
               <div class="result-list">
                  <div
                     class="result-item"
                     v-for="(item, index) in filteredHotels"
                     :key="'hotel-' + index"
                     :class="{ highlighted: highlightedIndex === index && highlightedSection === 'hotel' }"
                     v-html="highlightText(item.name)"
                     @click="selectResult(item)"></div>
               </div>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import { hotelService } from "@/service/index.js";
import debounce from "lodash.debounce";
import { getCookie, setCookie } from "@/utils";

export default {
   name: "SearchableDropdown",
   props: {
      placeholder: {
         type: String,
         default: "",
      },
   },
   data() {
      return {
         searchQuery: "",
         districts: [],
         hotels: [],
         searchHistory: [],
         highlightedIndex: -1,
         highlightedSection: "district",
         selectionMade: false,
      };
   },
   computed: {
      filteredDistricts() {
         return this.districts;
      },
      filteredHotels() {
         return this.hotels;
      },
      hasResults() {
         return this.searchHistory.length > 0 || this.filteredDistricts.length > 0 || this.filteredHotels.length > 0;
      },
      shouldHideInput() {
         // Sadece seçim yapıldıysa text'i gizle, layout'u koru.
         return this.selectionMade;
      },
   },
   methods: {
      handlerInputFocused() {
         this.$emit("opaq-selection", true);
         this.$emit("input-focused");
      },
      handlerInputBlurred() {
         this.$emit("opaq-selection", false);

         // Eğer dropdown açıksa VE gerçek arama sonuçları varsa (sadece history değil), blur'u bypass et
         const hasActualResults = this.districts.length > 0 || this.hotels.length > 0;
         if (hasActualResults) {
            return;
         }

         this.$emit("input-blurred");
      },
      focusInput() {
         this.$nextTick(() => {
            this.$refs.searchInput?.focus();
         });
      },
      handleInputImmediate(e) {
         const value = e.target.value;

         // Kullanıcı yazmaya başladığında hemen parent'a bildir (debounce yok)
         if (value.length > 0) {
            this.$emit("clear-selection");
         }

         // Debounced method'u çağır
         this.handleInputDebounced(e);
      },
      handleInputDebounced: debounce(function (e) {
         this.selectionMade = false;
         const value = e.target.value;

         if (value.length === 0) {
            this.districts = [];
            this.hotels = [];
            this.highlightedIndex = -1;
            this.highlightedSection = "district";
            return;
         }

         if (value.length >= 2) {
            this.$emit("autocomplete-pending", true);
            hotelService.hotelAutocomplete({ keyword: value }).then((response) => {
               this.districts = response.destinations.filter((item) => item.type === 1);
               this.hotels = response.destinations.filter((item) => item.type === 2);
               this.$emit("autocomplete-pending", false);
            });
         }
      }, 500),
      handleKeydown(e) {
         const activeSection = this.highlightedSection === "district" ? this.filteredDistricts : this.filteredHotels;

         // Arrow Down
         if (e.keyCode === 40) {
            e.preventDefault();
            if (this.highlightedIndex < activeSection.length - 1) {
               this.highlightedIndex++;
            } else if (this.highlightedSection === "district" && this.filteredHotels.length > 0) {
               this.highlightedSection = "hotel";
               this.highlightedIndex = 0;
            }
         }

         // Arrow Up
         if (e.keyCode === 38) {
            e.preventDefault();
            if (this.highlightedIndex > 0) {
               this.highlightedIndex--;
            } else if (this.highlightedSection === "hotel") {
               this.highlightedSection = "district";
               this.highlightedIndex = this.filteredDistricts.length - 1;
            }
         }

         // Enter
         if (e.keyCode === 13) {
            e.preventDefault();
            const selected = activeSection[this.highlightedIndex];
            if (selected) {
               this.selectResult(selected);
            }
         }
      },
      selectResult(result, isFromHistory = false) {
         if (!isFromHistory) {
            this.updateSearchHistory(result);
         }
         this.$emit("update-selection", result);
         this.selectionMade = true;
         this.searchQuery = "";
         this.districts = [];
         this.hotels = [];
      },
      clearInput() {
         this.searchQuery = "";
         this.districts = [];
         this.hotels = [];
         this.highlightedIndex = -1;
         this.selectionMade = false;
         this.$emit("update-selection", {});
      },
      highlightText(text) {
         if (!this.searchQuery) return text;
         const regex = new RegExp(this.searchQuery, "gi");
         return text.replace(regex, "<strong>$&</strong>");
      },
      loadSearchHistory() {
         const history = getCookie("hotelSearchHistory");
         if (history) {
            this.searchHistory = JSON.parse(history);
         }
      },
      updateSearchHistory(item) {
         if (this.searchHistory.find((h) => h.id === item.id)) return;

         if (this.searchHistory.length >= 5) {
            this.searchHistory.shift();
         }
         this.searchHistory.push(item);
         setCookie("hotelSearchHistory", JSON.stringify(this.searchHistory), 7, 0);
      },
   },
   mounted() {
      this.loadSearchHistory();
      this.focusInput();
   },
};
</script>

<style lang="scss" scoped>
.searchable-dropdown {
   width: 100%;
   min-width: 400px;
   // background-color: var(--container-bg);
   position: relative;
   top: -48px;
   z-index: 1010;

   .searchable-input-wrapper {
      position: relative;

      // padding: 1rem;
      // border-bottom: 1px solid var(--gray-200);

      .searchable-input {
         width: 100%;
         height: 48px;
         // border-radius: 0.5rem;
         // text-indent: 1rem;
         color: var(--gray-700);
         border: none !important;
         background: transparent !important;
         text-transform: capitalize;
         padding-left: 2.5rem;
         font-size: 16px !important;

         &:focus {
            border-color: var(--blue-500);
            outline: none;
         }

         &::placeholder {
            color: var(--gray-500);
         }

         &.invisible-input {
            visibility: hidden;
         }
      }

      .icon-location-point-solid {
         position: absolute;
         left: 1.5rem;
         top: 50%;
         transform: translateY(-50%);
      }

      .clear-button {
         position: absolute;
         right: 1.5rem;
         top: 50%;
         transform: translateY(-50%);
         cursor: pointer;
         padding: 0.25rem;
         background-color: var(--gray-100);
         border-radius: 50%;

         &:hover {
            background-color: var(--gray-200);
         }
      }
   }

   .searchable-results {
      max-height: 400px;
      overflow-y: auto;
      background-color: var(--surface-level-2);
      box-shadow: var(--gts-box-shadow);
      border: 2px solid var(--gray-200);

      .result-section {
         display: flex;
         padding: 1rem;
         border-bottom: 1px solid var(--gray-200);
         border-left: 4px solid transparent;

         &:last-child {
            border-bottom: none;
         }

         &.history-section {
            background-color: var(--gray-100);
            border-left-color: var(--gray-300);

            .result-item {
               color: var(--txt-color);

               &:hover {
                  background-color: var(--gray-300);
               }
            }
         }

         &.district-section {
            border-left-color: var(--orange-200);

            .section-icon i {
               color: var(--orange-300);
            }
         }

         &.hotel-section {
            border-left-color: var(--red-400);

            .section-icon i {
               color: var(--red-400);
            }
         }

         .section-icon {
            min-width: 64px;
            display: flex;
            align-items: flex-start;

            i {
               width: 40px;
               height: 40px;
            }
         }

         .section-content {
            flex: 1;

            .section-title {
               margin-bottom: 0.5rem !important;
               font-weight: 700;
               color: var(--gray-700);
            }

            .result-list {
               .result-item {
                  padding: 0.25rem 0.5rem;
                  cursor: pointer;
                  font-size: 14px;
                  color: var(--txt-color);
                  border-radius: 4px;

                  margin-left: -0.5rem;

                  &:hover,
                  &.highlighted {
                     background-color: var(--gray-300);
                     color: var(--txt-color);
                  }

                  ::v-deep strong {
                     font-weight: bold;
                  }
               }
            }
         }
      }
   }
}
</style>
